/**
 * Captures a Recharts chart as PNG blob
 * Works by converting SVG to Canvas (no external dependencies)
 *
 * @param selector - CSS selector for the chart container (should contain SVG)
 * @param options - Screenshot options
 * @returns Promise<Blob | null> - PNG blob or null if failed
 */
export const captureChartAsBlob = async (
  selector: string = '[data-chart-id="power-profile"]',
  options: {
    scale?: number; // Quality multiplier (2 = 2x resolution)
    quality?: number; // PNG quality 0-1
    backgroundColor?: string; // Background color
  } = {}
): Promise<Blob | null> => {
  const { scale = 2, quality = 0.95, backgroundColor = '#ffffff' } = options;

  return new Promise((resolve) => {
    const chartContainer = document.querySelector(selector);

    if (!chartContainer) {
      resolve(null);
      return;
    }

    const chartSvg = chartContainer.querySelector('svg');
    if (!chartSvg) {
      resolve(null);
      return;
    }

    try {
      const svgData = new XMLSerializer().serializeToString(chartSvg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const image = new Image();

      const cleanup = () => URL.revokeObjectURL(url);

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 5000);

      image.onload = () => {
        clearTimeout(timeout);

        try {
          const canvas = document.createElement('canvas');
          canvas.width = chartContainer.clientWidth * scale;
          canvas.height = chartContainer.clientHeight * scale;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            cleanup();
            resolve(null);
            return;
          }

          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(image, 0, 0);

          canvas.toBlob(
            (blob) => {
              cleanup();
              resolve(blob || null);
            },
            'image/png',
            quality
          );
        } catch (error) {
          console.error('Error capturing chart:', error);
          cleanup();
          resolve(null);
        }
      };

      image.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
      };

      image.src = url;
    } catch (error) {
      console.error('Error capturing chart:', error);
      resolve(null);
    }
  });
};

/**
 * Downloads blob as file (for testing purposes)
 *
 * @param blob - Blob to download
 * @param filename - Download filename
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
